import { useEffect, useMemo, useRef } from "react";
import { Loader2, Music2 } from "lucide-react";

// ── helpers ──────────────────────────────────────────────────────────────────

function getActiveLine(sections, currentTime) {
  for (let si = 0; si < sections.length; si++) {
    const lines = sections[si].lines || [];
    for (let li = 0; li < lines.length; li++) {
      const line = lines[li];
      if (currentTime >= (line.start || 0) && currentTime < (line.end || Infinity)) {
        return { sectionIndex: si, lineIndex: li };
      }
    }
  }
  return null;
}

function ChordChip({ chord, active }) {
  return (
    <span
      className={`inline-flex items-center rounded-md px-1.5 py-0.5 text-[11px] font-semibold transition-colors duration-200 ${
        active
          ? "bg-cyan-300/25 text-cyan-100"
          : "bg-white/[0.06] text-slate-400"
      }`}
    >
      {chord}
    </span>
  );
}

function LineRow({ line, isActive, currentTime }) {
  const ref = useRef(null);

  useEffect(() => {
    if (isActive && ref.current) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [isActive]);

  return (
    <div
      ref={ref}
      className={`transition-colors duration-300 rounded-lg px-3 py-1.5 ${
        isActive ? "bg-cyan-300/[0.08]" : ""
      }`}
    >
      {line.chord && (
        <p
          className={`mb-0.5 text-[11px] font-bold tracking-wider transition-colors duration-200 ${
            isActive ? "text-cyan-300" : "text-violet-300/70"
          }`}
        >
          {line.chord}
        </p>
      )}
      {line.text ? (
        <p
          className={`text-sm leading-relaxed transition-colors duration-200 ${
            isActive ? "text-white font-medium" : "text-slate-300"
          }`}
        >
          {line.text}
        </p>
      ) : line.chords?.length ? (
        <div className="flex flex-wrap gap-1 py-1">
          {line.chords.map((c, i) => (
            <ChordChip key={`${c}-${i}`} chord={c} active={isActive} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

// ── main component ────────────────────────────────────────────────────────────

/**
 * SongStructurePanel — shows song structure with real-time highlight.
 *
 * Props:
 *   structure   {object|null}   from GET /api/global-songs/{id}/structure
 *   currentTime {number}        seconds, from YouTube player
 *   onSectionClick {function}   (start_time) → seek
 *   loading     {bool}
 */
export default function SongStructurePanel({
  structure = null,
  currentTime = 0,
  onSectionClick,
  loading = false,
}) {
  const sections = useMemo(() => structure?.sections || [], [structure]);
  const hasLyrics = structure?.has_lyrics ?? false;

  const activePair = useMemo(
    () => getActiveLine(sections, currentTime),
    [sections, currentTime]
  );

  if (loading) {
    return (
      <div className="grid min-h-48 place-items-center">
        <Loader2 className="h-6 w-6 animate-spin text-cyan-200" />
      </div>
    );
  }

  if (!sections.length) {
    return (
      <div className="grid min-h-48 place-items-center rounded-2xl border border-white/10 bg-white/[0.025] p-6 text-center">
        <div>
          <Music2 className="mx-auto mb-3 h-7 w-7 text-slate-500" />
          <p className="text-sm text-slate-400">
            {hasLyrics
              ? "Estructura en procesamiento…"
              : "Estructura no disponible todavía para esta canción."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Section index nav */}
      <div className="steel-scroll-soft flex gap-2 overflow-x-auto pb-1">
        {sections.map((sec, si) => {
          const isActive = activePair?.sectionIndex === si;
          return (
            <button
              key={`${sec.name}-${si}`}
              type="button"
              onClick={() => onSectionClick?.(sec.start)}
              className={`shrink-0 rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors ${
                isActive
                  ? "border-cyan-300/40 bg-cyan-300/12 text-cyan-100"
                  : "border-white/10 text-slate-400 hover:text-slate-200"
              }`}
            >
              {sec.name || `Sección ${si + 1}`}
            </button>
          );
        })}
      </div>

      {/* Sections */}
      {sections.map((sec, si) => {
        const secLines = sec.lines || [];
        const secChords = sec.chords || [];
        const isActiveSec = activePair?.sectionIndex === si;

        return (
          <div
            key={`${sec.name}-${si}`}
            className={`rounded-2xl border p-4 transition-colors ${
              isActiveSec
                ? "border-cyan-300/20 bg-cyan-300/[0.04]"
                : "border-white/10 bg-white/[0.025]"
            }`}
          >
            {/* Section header */}
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => onSectionClick?.(sec.start)}
                className="text-xs font-bold uppercase tracking-widest text-cyan-200/70 hover:text-cyan-200"
              >
                {sec.name || `Sección ${si + 1}`}
              </button>
              {/* Chord summary chips */}
              {secChords.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {secChords.slice(0, 8).map((c, i) => (
                    <ChordChip key={`${c}-${i}`} chord={c} active={false} />
                  ))}
                </div>
              )}
            </div>

            {/* Lines */}
            {secLines.length > 0 ? (
              <div className="space-y-0.5">
                {secLines.map((line, li) => (
                  <LineRow
                    key={`${sec.name}-${li}`}
                    line={line}
                    isActive={activePair?.sectionIndex === si && activePair?.lineIndex === li}
                    currentTime={currentTime}
                  />
                ))}
              </div>
            ) : secChords.length > 0 ? (
              <div className="flex flex-wrap gap-1.5 pl-3">
                {secChords.map((c, i) => (
                  <span
                    key={`${c}-${i}`}
                    className="rounded-lg border border-white/10 bg-black/20 px-2.5 py-1 text-sm font-semibold text-slate-200"
                  >
                    {c}
                  </span>
                ))}
              </div>
            ) : (
              <p className="pl-3 text-xs text-slate-500">Sin datos disponibles para esta sección.</p>
            )}
          </div>
        );
      })}
    </div>
  );
}
